import { MantineComponent, Tabs as MantineTabs } from "@mantine/core";
import { componentWrapper } from "../../utilities";
import styles from "./tabs.module.scss";
import type { CustomTabsFactory, CustomTabsTabFactory, IProps, ITabsTabProps } from "./tabs.types";
import { ThemeExtend } from "@mantine/core/lib/core/factory/factory";
import { Icon } from "../icon";
import classnames from "classnames";

/**
 * Tabs component for organizing content into multiple sections.
 * @param {object | string} props.classNames - Custom class names for tab elements.
 * @returns {JSX.Element} The Tabs component.
 */
const Tabs = componentWrapper<CustomTabsFactory, IProps>("Tabs", ({ classNames, ...otherProps }, ref) => {
    return (
        <MantineTabs
            ref={ref}
            classNames={{
                root: classnames(styles["root"], typeof classNames === "object" ? classNames?.root : ""),
                list: classnames(styles["list"], typeof classNames === "object" ? classNames?.list : ""),
                panel: classnames(styles["panel"], typeof classNames === "object" ? classNames?.panel : ""),
                tab: classnames(styles["tab"], typeof classNames === "object" ? classNames?.tab : ""),
                tabLabel: classnames(styles["tabLabel"], typeof classNames === "object" ? classNames?.tabLabel : ""),
                tabSection: classnames(
                    styles["tabSection"],
                    typeof classNames === "object" ? classNames?.tabSection : ""
                ),
            }}
            {...otherProps}
        />
    );
});

/**
 * Individual tab component within a Tabs container.
 * @param {IconDefinition} props.icon - Optional icon to display on the left side of the tab.
 * @returns {JSX.Element} The TabsTab component.
 */
const TabsTab = componentWrapper<CustomTabsTabFactory, ITabsTabProps>("TabsTab", ({ icon, ...otherProps }, ref) => {
    return (
        <MantineTabs.Tab ref={ref} leftSection={icon ? <Icon size="sm" icon={icon} /> : undefined} {...otherProps} />
    );
}) as MantineComponent<CustomTabsTabFactory> & {
    extend: ThemeExtend<CustomTabsTabFactory>["extend"];
};

TabsTab.extend = MantineTabs.Tab.extend;

Tabs.Tab = TabsTab;
Tabs.Panel = MantineTabs.Panel;
Tabs.List = MantineTabs.List;

export { Tabs, TabsTab };
